import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, MoreVertical, Unlock, KeyRound, MailQuestion, Lock, ShieldOff, Ban, Trash2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Table } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLES = ["user", "admin", "editor", "social_media_manager", "social_manager",
  "marketing", "sales", "sales_manager", "sales_associate", "analyst", "financial",
  "financial_analyst", "it", "superadmin"];
const STAFF_ROLES = ["admin", "editor", "social_media_manager", "social_manager",
  "marketing", "sales", "sales_manager", "sales_associate", "analyst", "financial",
  "financial_analyst", "it"];

const OverridesButton = ({ u, onRefresh }: { u: any; onRefresh: () => void }) => {
  const { data: rbac } = useQuery({
    queryKey: ["admin-rbac"],
    queryFn: async () => (await api.get("/admin/rbac")).data,
  });
  const [local, setLocal] = useState<string[] | null>(null);
  const extra: string[] = local ?? (u.extra_permissions || []);
  const toggle = async (key: string) => {
    const next = extra.includes(key) ? extra.filter((k) => k !== key) : [...extra, key];
    setLocal(next);
    try {
      await api.patch(`/admin/users/${u.id}/permissions`, { extra_permissions: next });
      toast.success(`Overrides updated for ${u.email}`);
      onRefresh();
    } catch (e) { toast.error(apiError(e)); setLocal(extra); }
  };
  if (!rbac) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`overrides-button-${u.id}`}
          className={`font-mono text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border transition-colors ${
            extra.length
              ? "text-[#FBBF24] border-[#F59E0B]/40 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20"
              : "text-gray-500 border-white/10 hover:border-white/30 hover:text-gray-300"}`}>
          {extra.length ? `+${extra.length} override${extra.length === 1 ? "" : "s"}` : "+ overrides"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 bg-[#121212] border-white/15 p-3"
        data-testid={`overrides-popover-${u.id}`}>
        <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
          Extra areas for {u.full_name || u.email}
        </p>
        <p className="text-gray-600 text-[10px] mb-2 leading-snug">
          Granted on top of the {String(u.role).replace(/_/g, " ")} role — this person only.
        </p>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {rbac.sections.map((s: any) => (
            <label key={s.key} className="flex items-center gap-2 cursor-pointer hover:bg-white/[0.03] rounded px-1 py-0.5">
              <input type="checkbox" checked={extra.includes(s.key)} onChange={() => toggle(s.key)}
                data-testid={`override-${u.id}-${s.key}`}
                className="h-3.5 w-3.5 accent-[#F59E0B] cursor-pointer" />
              <span className="text-xs text-gray-300">{s.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const RolesPermissionsPanel = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: rbac } = useQuery({
    queryKey: ["admin-rbac"],
    queryFn: async () => (await api.get("/admin/rbac")).data,
  });
  const { data: requests } = useQuery({
    queryKey: ["access-requests"],
    queryFn: async () => (await api.get("/admin/access-requests?status=pending")).data,
    refetchInterval: 60_000,
  });
  const act = async (id: string, action: string) => {
    try {
      const res = await api.patch(`/admin/access-requests/${id}`, { action });
      toast.success(res.data.message);
      queryClient.invalidateQueries({ queryKey: ["access-requests"] });
      queryClient.invalidateQueries({ queryKey: ["admin-rbac"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (role: string, perms: string[], key: string) => {
    const next = perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];
    try {
      await api.put(`/admin/rbac/${role}`, { permissions: next });
      queryClient.invalidateQueries({ queryKey: ["admin-rbac"] });
      queryClient.invalidateQueries({ queryKey: ["my-permissions"] });
      toast.success(`${role.replace(/_/g, " ")} permissions updated`);
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!rbac) return null;
  return (
    <div className="mb-6 bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="roles-permissions-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-white text-base">Roles &amp; Permissions</h2>
          <p className="text-gray-500 text-xs mt-0.5 max-w-2xl">
            Tick which admin areas each role can access — enforced on every request, applied instantly,
            no re-login needed. Super Admin always has full access.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(!open)} data-testid="roles-panel-toggle"
          className="border-white/15 text-gray-300 hover:bg-white/5 bg-transparent">
          {open ? "Hide matrix" : "Show matrix"}
        </Button>
      </div>
      {(requests || []).length > 0 && (
        <div className="mt-4 space-y-2" data-testid="access-requests-list">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#F59E0B]">
            Pending access requests ({requests.length})
          </p>
          {requests.map((r: any) => (
            <div key={r.id} data-testid={`access-request-${r.id}`}
              className="flex flex-wrap items-center justify-between gap-2 bg-[#0B0B0B] border border-[#F59E0B]/25 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-300">
                <span className="text-white font-medium">{r.email}</span>
                <span className="text-gray-500"> ({String(r.role || "").replace(/_/g, " ")})</span>
                {" "}wants <span className="text-[#00E5FF]">{r.section}</span>
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => act(r.id, "approve")} data-testid={`approve-request-${r.id}`}
                  className="h-7 bg-[#4ADE80]/10 text-[#4ADE80] border border-[#4ADE80]/40 hover:bg-[#4ADE80]/20 text-xs">
                  Approve for role
                </Button>
                <Button size="sm" onClick={() => act(r.id, "approve_user")} data-testid={`approve-user-request-${r.id}`}
                  className="h-7 bg-[#F59E0B]/10 text-[#FBBF24] border border-[#F59E0B]/40 hover:bg-[#F59E0B]/20 text-xs">
                  Just this person
                </Button>
                <Button size="sm" onClick={() => act(r.id, "dismiss")} data-testid={`dismiss-request-${r.id}`}
                  className="h-7 bg-white/5 text-gray-400 border border-white/15 hover:text-red-400 hover:border-red-500/40 text-xs">
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="overflow-x-auto mt-4 -mx-1 px-1">
          <table className="w-full text-sm" data-testid="roles-permissions-matrix">
            <thead>
              <tr>
                <th className="text-left font-mono text-[10px] uppercase tracking-wider text-gray-500 px-2 py-2">Area</th>
                {rbac.roles.map((r: any) => (
                  <th key={r.role}
                    className="font-mono text-[10px] uppercase tracking-wider text-gray-400 px-2 py-2 whitespace-nowrap">
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rbac.sections.map((s: any) => (
                <tr key={s.key} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-2 py-2.5">
                    <p className="text-white text-xs font-medium whitespace-nowrap">{s.label}</p>
                    <p className="text-gray-600 text-[10px] max-w-[200px] leading-snug">{s.desc}</p>
                  </td>
                  {rbac.roles.map((r: any) => (
                    <td key={r.role} className="text-center px-2 py-2.5">
                      <input type="checkbox" checked={r.permissions.includes(s.key)}
                        onChange={() => toggle(r.role, r.permissions, s.key)}
                        data-testid={`perm-${r.role}-${s.key}`}
                        className="h-4 w-4 accent-[#00E5FF] cursor-pointer" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const INVITE_ROLES = ["user", "admin", "editor", "social_media_manager", "marketing",
  "sales", "sales_manager", "sales_associate", "analyst", "financial",
  "financial_analyst", "it"];

const CreateSeatCard = ({ onCreated, isSuper = false }: { onCreated: () => void; isSuper?: boolean }) => {
  const [form, setForm] = useState({ full_name: "", email: "", role: "user" });
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/users", form);
      res.data.invite_email === "sent" ? toast.success(res.data.message) : toast.warning(res.data.message);
      setForm({ full_name: "", email: "", role: "user" });
      onCreated();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-[#121212] border border-white/10 rounded-lg p-4 mb-6" data-testid="create-seat-card">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">
        <UserPlus className="h-3.5 w-3.5 text-[#00E5FF]" /> Create user seat
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={form.full_name} onChange={set("full_name")} placeholder="Full name"
          data-testid="seat-name-input" className="bg-black/40 border-white/10 text-white sm:max-w-[240px]" />
        <Input value={form.email} onChange={set("email")} type="email" placeholder="email@company.com"
          data-testid="seat-email-input" className="bg-black/40 border-white/10 text-white flex-1" />
        {isSuper && (
          <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="seat-role-select"
              className="bg-black/40 border-white/10 text-white sm:w-[210px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
              {INVITE_ROLES.map((r) => (
                <SelectItem key={r} value={r} data-testid={`seat-role-option-${r}`}>
                  {r.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button onClick={create} data-testid="seat-create-submit"
          disabled={busy || !form.full_name.trim() || !form.email.includes("@")}
          className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] shrink-0">
          {busy ? "Sending invite…" : "Create seat & send invite"}
        </Button>
      </div>
      <p className="font-mono text-[10px] text-gray-600 mt-2.5">
        They'll receive a welcome email with a personal link to set their password and sign in
        {isSuper ? " — pick a role and their access is correct from day one." : " — no passwords to share by hand."}
      </p>
    </div>
  );
};

const SetPasswordDialog = ({ target, onClose, onDone }: {
  target: any; onClose: () => void; onDone: () => void;
}) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/users/${target.id}/password`, { new_password: password });
      toast.success(res.data.message);
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="set-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4 text-[#00E5FF]" /> Set password for {target?.email}
          </DialogTitle>
          <DialogDescription className="text-gray-500 text-xs">
            The user will be required to choose a new password at their next login. Any pending reset links are invalidated.
          </DialogDescription>
        </DialogHeader>
        <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="New temporary password (min 8 characters)" autoFocus
          data-testid="set-password-input" className="bg-black/40 border-white/10 text-white font-mono" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="set-password-cancel"
            className="text-gray-400 hover:text-white hover:bg-white/5">Cancel</Button>
          <Button onClick={submit} disabled={busy || password.length < 8} data-testid="set-password-submit"
            className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            {busy ? "Saving…" : "Set password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RestrictDialog = ({ target, onClose, onDone }: {
  target: { u: any; mode: "suspend" | "limit" }; onClose: () => void; onDone: () => void;
}) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isSuspend = target.mode === "suspend";

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/users/${target.u.id}/restrictions`,
        { [isSuspend ? "suspended" : "limited"]: true, reason });
      toast.success(res.data.message);
      onDone();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="restrict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {isSuspend ? <ShieldOff className="h-4 w-4 text-orange-400" /> : <Ban className="h-4 w-4 text-yellow-400" />}
            {isSuspend ? "Suspend" : "Limit"} {target.u.email}
          </DialogTitle>
          <DialogDescription className="text-gray-500 text-xs">
            {isSuspend
              ? "They won't be able to sign in and any active session ends immediately."
              : "They can still sign in and read, but can't submit stories, apply as an author or request payouts."}
            {" "}The note below is shown to them{isSuspend ? " when they try to log in" : " at login and on blocked actions"}.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={3}
          placeholder="Reason shown to the user (optional)" autoFocus
          data-testid="restrict-reason-input" className="bg-black/40 border-white/10 text-white text-sm" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="restrict-cancel"
            className="text-gray-400 hover:text-white hover:bg-white/5">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="restrict-submit"
            className={isSuspend ? "bg-orange-500 text-black font-bold hover:bg-orange-400"
              : "bg-yellow-500 text-black font-bold hover:bg-yellow-400"}>
            {busy ? "Saving…" : isSuspend ? "Suspend account" : "Limit account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserActionsMenu = ({ u, isSelf, onSetPassword, onDelete, onRestrict, onRefresh }: {
  u: any; isSelf: boolean; onSetPassword: () => void; onDelete: () => void;
  onRestrict: (mode: "suspend" | "limit") => void; onRefresh: () => void;
}) => {
  const restrictable = !isSelf && u.role !== "superadmin";

  const resetLockout = async () => {
    try {
      const res = await api.patch(`/admin/users/${u.id}/reset-lockout`);
      toast.success(res.data.message);
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendReset = async () => {
    try {
      const res = await api.post(`/admin/users/${u.id}/send-reset`);
      res.data.email_status === "sent" ? toast.success(res.data.message) : toast.warning(res.data.message);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setRestriction = async (patch: Record<string, boolean>) => {
    try {
      const res = await api.patch(`/admin/users/${u.id}/restrictions`, patch);
      toast.success(res.data.message);
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`user-actions-trigger-${u.id}`}
          className="h-7 w-7 text-gray-500 hover:text-white hover:bg-white/10">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#121212] border-white/10 text-white w-56">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
          Account actions
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={resetLockout} data-testid={`user-action-reset-lockout-${u.id}`}
          className="text-sm gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
          <Unlock className="h-3.5 w-3.5 text-emerald-400" />
          Reset login lockout
          {u.locked && <span className="ml-auto font-mono text-[9px] text-red-400">LOCKED</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSetPassword} data-testid={`user-action-set-password-${u.id}`}
          className="text-sm gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
          <KeyRound className="h-3.5 w-3.5 text-[#00E5FF]" />
          Set password manually…
        </DropdownMenuItem>
        <DropdownMenuItem onClick={sendReset} data-testid={`user-action-send-reset-${u.id}`}
          className="text-sm gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
          <MailQuestion className="h-3.5 w-3.5 text-purple-400" />
          Send password reset email
        </DropdownMenuItem>
        {restrictable && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              onClick={() => (u.suspended ? setRestriction({ suspended: false }) : onRestrict("suspend"))}
              data-testid={`user-action-suspend-${u.id}`}
              className="text-sm gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
              <ShieldOff className="h-3.5 w-3.5 text-orange-400" />
              {u.suspended ? "Unsuspend account" : "Suspend account…"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => (u.limited ? setRestriction({ limited: false }) : onRestrict("limit"))}
              data-testid={`user-action-limit-${u.id}`}
              className="text-sm gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
              <Ban className="h-3.5 w-3.5 text-yellow-400" />
              {u.limited ? "Remove limits" : "Limit account…"}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={onDelete} data-testid={`user-action-delete-${u.id}`}
              className="text-sm gap-2 text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer">
              <Trash2 className="h-3.5 w-3.5" />
              Delete account…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const StatusCell = ({ u }: { u: any }) => {
  if (u.deleted)
    return (
      <span data-testid={`user-deleted-badge-${u.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-gray-500">
        <Trash2 className="h-2.5 w-2.5" /> Deleted
      </span>
    );
  const badges: React.ReactNode[] = [];
  if (u.locked)
    badges.push(
      <span key="locked" data-testid={`user-locked-badge-${u.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-red-500/15 border border-red-500/30 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-400">
        <Lock className="h-2.5 w-2.5" /> Locked
      </span>);
  if (u.suspended)
    badges.push(
      <span key="suspended" data-testid={`user-suspended-badge-${u.id}`}
        title={u.suspended_reason || undefined}
        className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 border border-orange-500/30 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-orange-400">
        <ShieldOff className="h-2.5 w-2.5" /> Suspended
      </span>);
  if (u.limited)
    badges.push(
      <span key="limited" data-testid={`user-limited-badge-${u.id}`}
        title={u.limited_reason || undefined}
        className="inline-flex items-center gap-1 rounded-full bg-yellow-500/15 border border-yellow-500/30 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-yellow-400">
        <Ban className="h-2.5 w-2.5" /> Limited
      </span>);
  if (badges.length) return <div className="flex flex-wrap items-center gap-1">{badges}</div>;
  if (u.force_password_change)
    return <span className="font-mono text-[10px] text-yellow-500/80" title="Must change password at next login">pw change due</span>;
  if (u.failed_attempts > 0)
    return <span className="font-mono text-[10px] text-gray-500" title="Recent failed login attempts">{u.failed_attempts} failed</span>;
  return <span className="font-mono text-[10px] text-gray-600">—</span>;
};

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "limited", label: "Limited" },
  { value: "locked", label: "Locked out" },
  { value: "deleted", label: "Deleted" },
];

const StatChip = ({ label, value, accent, testid }: {
  label: string; value: number; accent?: boolean; testid: string;
}) => (
  <div className="bg-[#121212] border border-white/10 rounded-lg px-4 py-3" data-testid={testid}>
    <p className={`font-mono text-2xl font-bold ${accent ? "text-[#00E5FF]" : "text-white"}`}>{value}</p>
    <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{label}</p>
  </div>
);

export default function Users() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [pwTarget, setPwTarget] = useState<any>(null);
  const [delTarget, setDelTarget] = useState<any>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [restrictTarget, setRestrictTarget] = useState<{ u: any; mode: "suspend" | "limit" } | null>(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const isSuper = me?.role === "superadmin";
  const canCreate = isSuper || !!me?.can_create_seats;

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isError } = useQuery({
    queryKey: ["admin-users", debounced, roleFilter, statusFilter, page],
    queryFn: async () => (await api.get("/admin/users", {
      params: {
        search: debounced || undefined,
        role: roleFilter === "all" ? undefined : roleFilter,
        status: statusFilter === "all" ? undefined : statusFilter,
        page,
      },
    })).data,
    enabled: isSuper,
    retry: false,
    placeholderData: (prev: any) => prev,
  });
  const users = data?.items;
  const stats = data?.stats;
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;
  const setFilter = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(1); };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-users"] });

  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      const res = await api.delete(`/admin/users/${delTarget.id}`);
      toast.success(res.data.message);
      setDelTarget(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setDelBusy(false);
  };

  const setRole = async (id: string, role: string) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast.success("Role updated");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setSeatPermission = async (id: string, allowed: boolean) => {
    try {
      const res = await api.patch(`/admin/users/${id}/seat-permission`, { can_create_seats: allowed });
      toast.success(res.data.message);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader title="Users"
        sub={isSuper ? "Manage accounts, internal roles and seat-creation rights"
          : canCreate ? "Create user seats for your team — the SuperAdmin manages roles"
            : "User management"} />
      {canCreate && <CreateSeatCard onCreated={refresh} isSuper={isSuper} />}
      {isSuper && <RolesPermissionsPanel />}
      {!isSuper ? (
        <p className="text-gray-500 text-sm" data-testid="users-restricted-note">
          {canCreate
            ? "Only the SuperAdmin can view all accounts and manage roles."
            : "You don't have permission to create user seats — ask the SuperAdmin to grant it."}
        </p>
      ) : isError ? (
        <p className="text-yellow-400 text-sm">SuperAdmin access required for user management.</p>
      ) : (
        <>
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <StatChip label="Total accounts" value={stats.total} accent testid="users-stat-total" />
              <StatChip label="New (7 days)" value={stats.new_7d} testid="users-stat-new" />
              <StatChip label="Restricted" value={stats.restricted} testid="users-stat-restricted" />
              <StatChip label="Deleted" value={stats.deleted} testid="users-stat-deleted" />
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email…" data-testid="users-search-input"
                className="bg-[#121212] border-white/10 text-white pl-9" />
            </div>
            <Select value={roleFilter} onValueChange={setFilter(setRoleFilter)}>
              <SelectTrigger className="bg-[#121212] border-white/10 text-white sm:w-[170px]"
                data-testid="users-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                <SelectItem value="all">All roles</SelectItem>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setFilter(setStatusFilter)}>
              <SelectTrigger className="bg-[#121212] border-white/10 text-white sm:w-[170px]"
                data-testid="users-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Table headers={["Name", "Email", "Role", "Seat creation", "Status", "Joined", ""]}>
          {(users || []).length === 0 && (
            <tr><td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-gray-600"
              data-testid="users-empty-state">No accounts match these filters</td></tr>
          )}
          {(users || []).map((u: any) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`}>
              <td className="px-4 py-2.5 text-white">
                <span className={u.deleted ? "text-gray-600 line-through" : ""}>{u.full_name}</span>
              </td>
              <td className="px-4 py-2.5 text-gray-400">
                <span className={u.deleted ? "text-gray-600" : ""}>{u.email}</span>
              </td>
              <td className="px-4 py-2.5">
                {u.deleted ? (
                  <span className="font-mono text-[10px] text-gray-600">—</span>
                ) : u.id === me?.id ? (
                  <span className="font-mono text-xs text-[#00E5FF] uppercase">{u.role} (you)</span>
                ) : (
                  <div className="space-y-1">
                    <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                      <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white w-[160px] h-8 text-xs"
                        data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10 text-white">
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {STAFF_ROLES.includes(u.role) && <OverridesButton u={u} onRefresh={refresh} />}
                  </div>
                )}
              </td>
              <td className="px-4 py-2.5">
                {STAFF_ROLES.includes(u.role) ? (
                  <Switch checked={!!u.can_create_seats}
                    onCheckedChange={(v) => setSeatPermission(u.id, v)}
                    data-testid={`seat-permission-toggle-${u.id}`}
                    className="data-[state=checked]:bg-[#00E5FF]" />
                ) : (
                  <span className="font-mono text-[10px] text-gray-600" title={u.role === "superadmin" ? "SuperAdmins can always create seats" : "Grantable for admin team roles only"}>
                    {u.role === "superadmin" ? "always" : "—"}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5"><StatusCell u={u} /></td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{timeAgo(u.created_at)}</td>
              <td className="px-2 py-2.5 text-right">
                {!u.deleted && (
                  <UserActionsMenu u={u} isSelf={u.id === me?.id}
                    onSetPassword={() => setPwTarget(u)} onDelete={() => { setDelConfirm(""); setDelTarget(u); }}
                    onRestrict={(mode) => setRestrictTarget({ u, mode })} onRefresh={refresh} />
                )}
              </td>
            </tr>
          ))}
          </Table>
          <div className="flex items-center justify-between mt-3">
            <p className="font-mono text-[11px] text-gray-500" data-testid="users-total-count">
              {total === 0 ? "0 accounts" : `Showing ${(page - 1) * 50 + 1}–${Math.min(page * 50, total)} of ${total} account${total === 1 ? "" : "s"}`}
            </p>
            {pages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" disabled={page <= 1} onClick={() => setPage(page - 1)}
                  data-testid="users-prev-page" className="h-7 w-7 text-gray-400 hover:text-white hover:bg-white/10">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-mono text-[11px] text-gray-500">{page} / {pages}</span>
                <Button variant="ghost" size="icon" disabled={page >= pages} onClick={() => setPage(page + 1)}
                  data-testid="users-next-page" className="h-7 w-7 text-gray-400 hover:text-white hover:bg-white/10">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </>
      )}
      {pwTarget && (
        <SetPasswordDialog target={pwTarget} onClose={() => setPwTarget(null)} onDone={refresh} />
      )}
      {restrictTarget && (
        <RestrictDialog target={restrictTarget} onClose={() => setRestrictTarget(null)} onDone={refresh} />
      )}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <Trash2 className="h-4 w-4 text-red-400" /> Delete {delTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 text-xs">
              The account is deactivated and anonymized — they can never sign in again. Their published
              content and submissions stay on the site. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
              Type <span className="text-red-400">{delTarget?.email}</span> to confirm
            </p>
            <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
              placeholder={delTarget?.email} data-testid="delete-confirm-input"
              className="bg-black/40 border-white/10 text-white font-mono text-sm" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel"
              className="bg-transparent border-white/10 text-gray-400 hover:text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}
              disabled={delBusy || delConfirm.trim().toLowerCase() !== (delTarget?.email || "").toLowerCase()}
              data-testid="delete-user-confirm"
              className="bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-40">
              {delBusy ? "Deleting…" : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
